import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { supabase } from './supabaseClient';
import {
  Box,
  Container,
  Grid,
  Paper,
  Typography,
  Button,
  Stack,
  Divider,
  Avatar,
} from '@mui/material';

const visitorBands = [
  'Up to 1 000',
  '1 000 - 9 999',
  '10 000 - 49 999',
  '50 000 - 99 999',
  '100 000 or more',
  "I don't know",
];

const platforms = [
  { key: 'prestashop', label: 'PrestaShop', icon: '🛍️' },
  { key: 'bigcommerce', label: 'BigCommerce', icon: '△' },
  { key: 'woocommerce', label: 'WooCommerce', icon: '🅆' },
  { key: 'custom', label: 'Custom website', icon: '🌐' },
  { key: 'shopify', label: 'Shopify', icon: '🛒' },
  { key: 'webflow', label: 'Webflow', icon: '𝑊' },
  { key: 'wordpress', label: 'WordPress', icon: '𝑊' },
  { key: 'other', label: 'Other', icon: '⋯' },
];

const agentBands = ['Just me', '2 - 5', '6 - 19', '20 or more'];

export default function OnboardingWebsite() {
  const navigate = useNavigate();
  const [visitors, setVisitors] = useState('');
  const [platform, setPlatform] = useState('');
  const [agents, setAgents] = useState('');
  const [saving, setSaving] = useState(false);

  const handleContinue = async () => {
    setSaving(true);
    try {
      try {
        localStorage.setItem('onboarding_website_visitors', visitors);
        localStorage.setItem('onboarding_website_platform', platform);
        localStorage.setItem('onboarding_support_agents', agents);
      } catch (_) {}

      // Persist to DB (idempotent upsert)
      try {
        const { data: { session } } = await supabase.auth.getSession();
        const userId = session?.user?.id;
        if (userId) {
          await supabase
            .from('user_onboarding')
            .upsert({
              user_id: userId,
              website_visitors_band: visitors || null,
              website_platform: platform || null,
              support_agents_band: agents || null,
            }, { onConflict: 'user_id' });
        }
      } catch (e) {
        // Non-blocking
        console.warn('[OnboardingWebsite] Failed to persist onboarding answers:', e);
      }
    } finally {
      setSaving(false);
      navigate('/onboarding/profile-4');
    }
  };

  return (
    <Container maxWidth="lg" sx={{ py: 6 }}>
      <Grid container spacing={4}>
        <Grid item xs={12} md={7}>
          <Paper variant="outlined" sx={{ p: 3 }}>
            <Stack spacing={4}>
              <div>
                <Typography variant="h6" gutterBottom>How many visitors does your website attract each month?</Typography>
                <Stack direction="row" spacing={2} useFlexGap flexWrap="wrap">
                  {visitorBands.map((band) => (
                    <Button
                      key={band}
                      variant={visitors === band ? 'contained' : 'outlined'}
                      onClick={() => setVisitors(band)}
                      sx={{ borderRadius: 2 }}
                    >
                      {band}
                    </Button>
                  ))}
                </Stack>
              </div>

              <div>
                <Typography variant="h6" gutterBottom>What platform powers your website?</Typography>
                <Stack direction="row" spacing={2} useFlexGap flexWrap="wrap">
                  {platforms.map((p) => (
                    <Button
                      key={p.key}
                      variant={platform === p.key ? 'contained' : 'outlined'}
                      onClick={() => setPlatform(p.key)}
                      sx={{ borderRadius: 2, display: 'flex', alignItems: 'center', gap: 1, minWidth: 180, justifyContent: 'flex-start', p: 1.5 }}
                    >
                      <Avatar sx={{ width: 24, height: 24, fontSize: 14 }}>{p.icon}</Avatar>
                      {p.label}
                    </Button>
                  ))}
                </Stack>
              </div>

              <div>
                <Typography variant="h6" gutterBottom>How many customer support agents do you have in your current setup?</Typography>
                <Stack direction="row" spacing={2} useFlexGap flexWrap="wrap">
                  {agentBands.map((band) => (
                    <Button
                      key={band}
                      variant={agents === band ? 'contained' : 'outlined'}
                      onClick={() => setAgents(band)}
                      sx={{ borderRadius: 2 }}
                    >
                      {band}
                    </Button>
                  ))}
                </Stack>
              </div>

              <Divider />

              <Stack direction="row" spacing={2}>
                <Button onClick={() => navigate('/onboarding/profile-2')}>Back</Button>
                <Box sx={{ flexGrow: 1 }} />
                <Button variant="contained" onClick={handleContinue} disabled={saving}>Continue</Button>
              </Stack>
            </Stack>
          </Paper>
        </Grid>

        <Grid item xs={12} md={5}>
          <Paper elevation={1} sx={{ p: 2, height: { xs: 320, md: 560 }, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <Box sx={{ position: 'relative', width: '100%', maxWidth: 520 }}>
              <Box
                component="img"
                src="https://images.unsplash.com/photo-1521737604893-d14cc237f11d?q=80&w=1200&auto=format&fit=crop"
                alt="Website and analytics"
                sx={{ width: '100%', borderRadius: 2, display: 'block' }}
              />
              <Box sx={{ position: 'absolute', right: -10, top: 20, bgcolor: 'background.paper', borderRadius: 2, boxShadow: 2, p: 1 }}>
                <Typography variant="caption">14.5k ↗</Typography>
              </Box>
              <Stack spacing={1} sx={{ position: 'absolute', left: -6, top: 80 }}>
                <Box sx={{ bgcolor: 'background.paper', borderRadius: 2, boxShadow: 2, p: 1.2 }}>
                  <Typography variant="caption">💬</Typography>
                </Box>
                <Box sx={{ bgcolor: 'background.paper', borderRadius: 2, boxShadow: 2, p: 1.2 }}>
                  <Typography variant="caption">▭▭▭</Typography>
                </Box>
              </Stack>
            </Box>
          </Paper>
        </Grid>
      </Grid>
    </Container>
  );
}





